import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PatientFormData } from './patientForm.types';

interface AssignmentSectionProps {
  form: UseFormReturn<PatientFormData>;
  practitioners: Array<{ id: string; full_name: string }> | undefined;
  assignedPractitioner?: string;
  isGuided?: boolean;
}

const AssignmentSection = ({ form, practitioners, assignedPractitioner, isGuided }: AssignmentSectionProps) => {
  const hasPractitioners = practitioners && practitioners.length > 0;
  const showDropdown = !assignedPractitioner && hasPractitioners;
  const showGuidedMessage = !assignedPractitioner && isGuided && !hasPractitioners;

  return (
    <div className='space-y-4'>
      <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>Assignment</h3>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <div className='space-y-1.5'>
          <Label htmlFor='status'>
            Status <span className='text-red-500'>*</span>
          </Label>
          <Select
            name='status'
            value={form.watch('status')}
            onValueChange={value => form.setValue('status', value || '', { shouldValidate: true })}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='active'>Active</SelectItem>
              <SelectItem value='inactive'>Inactive</SelectItem>
            </SelectContent>
          </Select>
          {form.formState.errors.status && (
            <p className='text-[0.8rem] text-red-500'>{form.formState.errors.status.message}</p>
          )}
        </div>
        {showGuidedMessage && (
          <div className='flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3'>
            <Info className='mt-0.5 h-4 w-4 shrink-0 text-slate-500' />
            <p className='text-[0.8rem] text-slate-600'>
              A provider will be assigned when a prescription is written.
            </p>
          </div>
        )}
        {showDropdown && (
          <div className='space-y-1.5'>
            <Label htmlFor='practitionerIdentifier'>
              Assigned Practitioner {!isGuided && <span className='text-red-500'>*</span>}
            </Label>
            <Select
              name='practitionerIdentifier'
              value={form.watch('practitioner_id')}
              onValueChange={value =>
                form.setValue('practitioner_id', value || '', { shouldValidate: true })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Select practitioner' />
              </SelectTrigger>
              <SelectContent>
                {practitioners.map(practitioner => (
                  <SelectItem key={practitioner.id} value={practitioner.id}>
                    {practitioner.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.practitioner_id && (
              <p className='text-[0.8rem] text-red-500'>{form.formState.errors.practitioner_id.message}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentSection;
